import { NoSessionError, refresh, type TokenResponse } from './turnstile-api';

/**
 * The identity token holder.
 *
 * The token lives in memory and **nowhere else**. The durable half of a login
 * is Turnstile's httpOnly session cookie, which JavaScript cannot read; a copy
 * of the token in `localStorage` would add an XSS-readable bearer credential
 * without making a single reload survive any better, because a reload asks
 * Turnstile for a fresh one anyway.
 *
 * Framework-free on purpose: Apollo's auth link needs the current token on
 * every request and must not be re-created when React re-renders.
 */

/** Refresh this far ahead of `exp` so a request going out does not race it. */
const EXPIRY_LEEWAY_SECONDS = 30;

export interface Identity {
  personId: string;
  email: string | null;
}

interface Held {
  token: string;
  expiresAt: number;
  identity: Identity;
}

let held: Held | null = null;
// Coalesces concurrent refreshes: N expired requests trigger one round trip.
let inFlight: Promise<string | null> | null = null;
const listeners = new Set<(identity: Identity | null) => void>();

export function currentIdentity(): Identity | null {
  return held?.identity ?? null;
}

export function subscribe(listener: (identity: Identity | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function publish(): void {
  const identity = currentIdentity();
  listeners.forEach((listener) => listener(identity));
}

export function adopt(response: TokenResponse): Identity {
  const claims = decodeClaims(response.identity_token);
  held = {
    token: response.identity_token,
    expiresAt: claims.exp ?? Date.now() / 1000 + response.expires_in,
    identity: { personId: claims.sub ?? '', email: claims.email ?? null },
  };
  publish();
  return held.identity;
}

export function clear(): void {
  held = null;
  publish();
}

/** The token to send with the next request, refreshing it first if it is
 *  spent. Returns null when nobody is logged in — a caller decides whether
 *  that is an error or a redirect. */
export async function accessToken(): Promise<string | null> {
  if (held && !isExpiring(held.expiresAt)) {
    return held.token;
  }
  return renew();
}

/** Restore a session from the cookie on a cold load. Null means never
 *  logged in, which is the ordinary first-visit answer. */
export async function restore(): Promise<Identity | null> {
  await renew();
  return currentIdentity();
}

function renew(): Promise<string | null> {
  inFlight ??= refresh()
    .then((response) => {
      adopt(response);
      return held?.token ?? null;
    })
    .catch((error: unknown) => {
      if (error instanceof NoSessionError) {
        clear();
        return null;
      }
      throw error;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

function isExpiring(expiresAt: number, leeway = EXPIRY_LEEWAY_SECONDS): boolean {
  return Date.now() / 1000 >= expiresAt - leeway;
}

interface Claims {
  sub?: string;
  email?: string;
  exp?: number;
}

/**
 * Read the claims the UI displays. This is **not** verification — the token is
 * verified by Keystone against Turnstile's JWKS, and nothing here may be
 * treated as a security decision. An unreadable token yields empty claims and
 * falls back to the server-stated `expires_in`.
 */
function decodeClaims(token: string): Claims {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return {};
    }
    // Base64url without padding; atob rejects both in some engines.
    const normalized = payload
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=');
    return JSON.parse(atob(normalized)) as Claims;
  } catch {
    return {};
  }
}
